import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { useApiError } from '../../lib/useApiError';
import { useCompleteInspection, useOrderInspections } from '../../lib/queries';
import { Sheet } from '../Sheet';
import { Alert, Button, Skeleton } from '../ui';
import { InspectionCapture, type CaptureState } from './InspectionCapture';
import type { VinVehicle } from './VinPanel';
import { InspectionGallery } from './InspectionGallery';

/** The photos already on record for a car on the floor, in a sheet. */
export function InspectionsSheet({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { data, isPending } = useOrderInspections(orderId);
  return (
    <Sheet open onClose={onClose} title={t('inspection.orderSection')}>
      {isPending ? <Skeleton className="h-40" /> : (data?.items ?? []).length === 0
        ? <p className="text-sm text-ink-dim">{t('inspection.notTaken')}</p>
        : (
          <div className="space-y-5">
            {data!.items.map((inspection) => <InspectionGallery key={inspection.id} inspection={inspection} />)}
          </div>
        )}
    </Sheet>
  );
}

/**
 * Photographing a car by its order: the walk-round before it goes back to its
 * owner, or an intake whose photos were left for later. Opened from the hand
 * over button when the studio requires it, finishing carries on to the till —
 * or the car can be handed over now and photographed afterwards.
 */
export function PhotosSheet({ orderId, kind, vehicle, thenHandOver = false, onClose, onDone, onLater }: {
  orderId: string;
  kind: 'INTAKE' | 'HANDOVER';
  vehicle?: VinVehicle | undefined;
  thenHandOver?: boolean;
  onClose: () => void;
  onDone: () => void;
  /** Hand the car over without the photos; offered only on the way to the till. */
  onLater?: (() => void) | undefined;
}) {
  const { t } = useI18n();
  const errorText = useApiError();
  const [state, setState] = useState<CaptureState | null>(null);
  const complete = useCompleteInspection(state?.inspectionId ?? '');
  const ready = Boolean(state?.inspectionId) && state!.missing.length === 0 && !state!.busy;

  return (
    <Sheet
      open
      onClose={onClose}
      title={kind === 'INTAKE' ? t('inspection.intakeTitleLater') : t('inspection.handoverTitle')}
      footer={(
        <div className="space-y-2">
          {complete.error instanceof ApiError && <Alert>{errorText(complete.error) ?? complete.error.message}</Alert>}
          <Button
            size="lg"
            className="w-full"
            disabled={!ready}
            loading={complete.isPending}
            onClick={() => complete.mutate(undefined, { onSuccess: onDone })}
          >
            {thenHandOver ? t('inspection.finishAndHandOver') : t('inspection.finish')}
          </Button>
          {onLater && (
            <Button variant="quiet" className="w-full" onClick={onLater}>{t('inspection.handOverWithoutPhotos')}</Button>
          )}
        </div>
      )}
    >
      <InspectionCapture
        target={kind === 'INTAKE' ? { kind, orderId } : { kind, orderId }}
        vehicle={kind === 'INTAKE' ? vehicle : undefined}
        onState={setState}
      />
    </Sheet>
  );
}
